// WorkflowStepCard — one step in the workflow builder canvas: a role Select and
// a list of response→routing rows. Fully controlled; every edit produces a new
// step object handed back via onChange. Server ids are owned by the builder's
// payload mapper, so this card only deals with the local BuilderStep model.

import { useEffect, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  IconButton,
  Icon,
  SelectDropdown,
  Input,
  type SelectDropdownOption,
} from '@/components/common';
import { WorkflowElementActionType } from '@/api/endpoints/workflowsApi';
import type { BuilderResponse, BuilderStep } from './WorkflowBuilder';

interface WorkflowStepCardProps {
  step: BuilderStep;
  index: number;
  /** All steps — the routing dropdown targets any of them. */
  steps: BuilderStep[];
  roleOptions: SelectDropdownOption[];
  responseOptions: SelectDropdownOption[];
  canDelete: boolean;
  onChange: (next: BuilderStep) => void;
  onDelete: () => void;
}

const routeValue = (r: BuilderResponse): string => {
  if (r.action === WorkflowElementActionType.NextStep) return `step:${r.nextStepRefId}`;
  if (r.action === WorkflowElementActionType.BackToUser) return 'back';
  return 'end';
};

/** The routing target's glyph, per the design: a step hands the submission to a
 *  person, "back to filler" returns it, and end stops the flow (same `flag` the
 *  canvas's End node uses). */
const routeIcon = (r: BuilderResponse): string => {
  if (r.action === WorkflowElementActionType.NextStep) return 'user-round-check';
  if (r.action === WorkflowElementActionType.BackToUser) return 'corner-up-left';
  return 'flag';
};

const WorkflowStepCard = ({
  step,
  index,
  steps,
  roleOptions,
  responseOptions,
  canDelete,
  onChange,
  onDelete,
}: WorkflowStepCardProps) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(step.title);

  useEffect(() => setDraftTitle(step.title), [step.title]);

  const commitTitle = () => {
    const next = draftTitle.trim() || step.title;
    onChange({ ...step, title: next });
    setDraftTitle(next);
    setEditing(false);
  };
  const onTitleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitTitle();
    } else if (e.key === 'Escape') {
      setDraftTitle(step.title);
      setEditing(false);
    }
  };

  const setResponses = (responses: BuilderResponse[]) => onChange({ ...step, responses });
  const addResponse = () =>
    setResponses([
      ...step.responses,
      {
        id: null,
        elementGlobalId: null,
        responseTitle: '',
        action: WorkflowElementActionType.EndWorkflow,
        nextStepId: null,
        nextStepRefId: null,
        nextStepReferenceId: null,
      },
    ]);
  const editResponse = (i: number, patch: Partial<BuilderResponse>) =>
    setResponses(step.responses.map((r, ri) => (ri === i ? { ...r, ...patch } : r)));
  const removeResponse = (i: number) => setResponses(step.responses.filter((_, ri) => ri !== i));

  const onRouteChange = (i: number, value: string) => {
    if (value === 'end')
      editResponse(i, {
        action: WorkflowElementActionType.EndWorkflow,
        nextStepRefId: null,
        nextStepId: null,
      });
    else if (value === 'back')
      editResponse(i, {
        action: WorkflowElementActionType.BackToUser,
        nextStepRefId: null,
        nextStepId: null,
      });
    else
      editResponse(i, {
        action: WorkflowElementActionType.NextStep,
        nextStepRefId: value.slice('step:'.length),
        nextStepId: null,
      });
  };

  // Routing options: every OTHER step, then End workflow / Back to user.
  const routeOptions: SelectDropdownOption[] = [
    ...steps
      .filter((s) => s.stepRefId !== step.stepRefId)
      .map((s, i) => ({ value: `step:${s.stepRefId}`, label: s.title || `${i + 1}` })),
    { value: 'end', label: t('forms_workflow_route_end') },
    { value: 'back', label: t('forms_workflow_route_back') },
  ];

  return (
    <div className='workflow-builder__step'>
      <div className='workflow-builder__step-head'>
        <span className='workflow-builder__step-badge'>{index + 1}</span>
        {editing ? (
          <Input
            className='workflow-builder__step-title-input'
            size='sm'
            autoFocus
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={onTitleKeyDown}
          />
        ) : (
          <span className='workflow-builder__step-title'>{step.title}</span>
        )}
        <span className='workflow-builder__step-actions'>
          <IconButton
            icon='pencil'
            variant='ghost'
            size='sm'
            aria-label={t('forms_workflow_step_rename')}
            onClick={() => setEditing(true)}
          />
          {canDelete && (
            <IconButton
              icon='trash-2'
              variant='ghost'
              size='sm'
              aria-label={t('forms_workflow_step_delete')}
              onClick={onDelete}
            />
          )}
        </span>
      </div>

      {/* Everything below the tinted head band sits in its own padded body —
          the head is flush to the card's edges, as the design draws it. */}
      <div className='workflow-builder__step-body'>
        <div className='workflow-builder__field'>
          <span className='workflow-builder__field-label'>{t('forms_workflow_role')}</span>
          <SelectDropdown
            size='sm'
            aria-label={t('forms_workflow_role')}
            value={step.roleId}
            onChange={(next) => onChange({ ...step, roleId: next })}
            options={[{ value: '', label: t('forms_workflow_role_ph') }, ...roleOptions]}
          />
        </div>

        <div className='workflow-builder__responses'>
          <div className='workflow-builder__responses-head'>
            <span className='workflow-builder__field-label'>{t('forms_workflow_routing')}</span>
            <Button
              className='workflow-builder__add-response'
              variant='ghost'
              size='sm'
              iconStart='plus'
              onClick={addResponse}
            >
              {t('forms_workflow_add')}
            </Button>
          </div>

          {step.responses.length === 0 ? (
            <p className='workflow-builder__empty'>{t('forms_workflow_no_responses')}</p>
          ) : (
            <ul className='workflow-builder__response-list'>
              {step.responses.map((r, i) => (
                // Each response and the target it routes to read as one unit, so
                // the pair sits in its own tinted block rather than as two loose
                // rows — the design's grouping.
                <li key={i} className='workflow-builder__response'>
                  <div className='workflow-builder__response-row'>
                    <SelectDropdown
                      size='sm'
                      iconStart='list-checks'
                      aria-label={t('forms_workflow_response_ph')}
                      value={r.responseTitle}
                      onChange={(next) => editResponse(i, { responseTitle: next })}
                      options={[
                        { value: '', label: t('forms_workflow_response_ph') },
                        ...responseOptions,
                      ]}
                    />
                    <IconButton
                      icon='x'
                      variant='ghost'
                      size='sm'
                      aria-label={t('forms_workflow_response_remove')}
                      onClick={() => removeResponse(i)}
                    />
                  </div>
                  <div className='workflow-builder__route'>
                    <Icon
                      name='corner-down-right'
                      size={16}
                      className='workflow-builder__route-glyph'
                      aria-hidden='true'
                    />
                    <SelectDropdown
                      // A step target is the flow continuing, so the design
                      // weights it heavier than the two terminal routes.
                      className={
                        r.action === WorkflowElementActionType.NextStep
                          ? 'workflow-builder__route-select--step'
                          : undefined
                      }
                      size='sm'
                      iconStart={routeIcon(r)}
                      aria-label={t('forms_workflow_routing')}
                      value={routeValue(r)}
                      onChange={(next) => onRouteChange(i, next)}
                      options={routeOptions}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkflowStepCard;
